import { type Editor, Extension } from "@tiptap/core";
import type { AskTextOptions } from "../../components/ui/PromptDialog";

export type { AskTextOptions };
export type DocLinkItem = { code: string; title: string; spaceName: string };

/** Everything a node view or editor plugin needs from the app. Kept in editor storage so node views can reach it. */
export type EditorHost = {
  labels: { note: string; warning: string };
  attachmentUrl: (src: string) => string;
  uploadImage: (file: File) => Promise<{ src: string }>;
  searchDocs: (query: string) => Promise<DocLinkItem[]>;
  openDoc: (code: string) => void;
  notify: (message: string, tone?: "info" | "warning" | "error") => void;
  askText: (options: AskTextOptions) => Promise<string | null>;
};

const noopHost: EditorHost = {
  labels: { note: "Note", warning: "Warning" },
  attachmentUrl: () => "",
  uploadImage: async () => {
    throw new Error("Uploads are not available here");
  },
  searchDocs: async () => [],
  openDoc: () => {},
  notify: () => {},
  askText: async () => null,
};

type HostStorage = { host: EditorHost };

export const HostExtension = Extension.create<{ host: EditorHost }, HostStorage>({
  name: "klarityHost",
  addOptions() {
    return { host: noopHost };
  },
  addStorage() {
    return { host: this.options.host };
  },
});

function storage(editor: Editor): HostStorage | undefined {
  return (editor.storage as unknown as Record<string, HostStorage | undefined>).klarityHost;
}

export function getHost(editor: Editor): EditorHost {
  return storage(editor)?.host ?? noopHost;
}

export function setHost(editor: Editor, host: EditorHost): void {
  const s = storage(editor);
  if (s) s.host = host;
}
